<template>
  <b-card class="mb-1">
    <div class="d-flex align-items-center justify-content-between mb-50">
      <h5 class="mb-0">
        Comprobantes de transferencia
      </h5>
      <b-badge
        v-if="pendingCount > 0"
        variant="warning"
      >
        {{ pendingCount }} pendientes
      </b-badge>
    </div>

    <!-- Upload (cliente o admin si quieres permitirlo) -->
    <div v-if="canUpload">
      <b-form-group
        label="Monto de la transferencia (MXN)"
        label-for="receiptAmount"
      >
        <b-form-input
          id="receiptAmount"
          v-model="amount"
          type="number"
          min="0"
          step="0.01"
          placeholder="0.00"
          :disabled="uploading"
        />
      </b-form-group>

      <b-form-group
        label="Nota (opcional)"
        label-for="receiptNote"
      >
        <b-form-textarea
          id="receiptNote"
          v-model="note"
          rows="2"
          :disabled="uploading"
        />
      </b-form-group>

      <b-form-group
        label="Comprobante"
        :label-for="`walletReceiptFile-${customerId}`"
        description="JPG, PNG o PDF de hasta 7 MB."
      >
        <b-form-file
          :id="`walletReceiptFile-${customerId}`"
          :key="fileInputKey"
          v-model="file"
          accept=".jpg,.jpeg,.png,.pdf"
          placeholder="Selecciona el comprobante"
          drop-placeholder="Suelta el archivo aquí"
          browse-text="Buscar"
          :disabled="uploading"
          @input="checkFile"
        />
      </b-form-group>

      <b-button
        variant="success"
        block
        :disabled="!canApply"
        @click="applyReceipt"
      >
        <b-spinner
          v-if="uploading"
          small
          class="mr-50"
        />
        {{ uploading ? 'Enviando...' : 'Aplicar comprobante' }}
      </b-button>
      <small
        v-if="applyHint"
        class="d-block text-muted text-center mt-50 mb-1"
      >
        {{ applyHint }}
      </small>
      <div
        v-else
        class="mb-1"
      />
    </div>

    <!-- List -->
    <div v-if="receipts.length">
      <div
        v-for="r in receipts"
        :key="r.id"
        class="border rounded p-75 mb-50"
      >
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <div><strong>{{ formatMoney(r.amount) }}</strong></div>
            <small class="text-muted">{{ r.created_at }}</small>
            <div v-if="r.note">
              <small>{{ r.note }}</small>
            </div>
          </div>

          <div class="text-right">
            <b-badge :variant="badgeVariant(r.status)">
              {{ r.status }}
            </b-badge>
            <div class="mt-25">
              <b-button
                v-if="r.attachment_url"
                size="sm"
                variant="outline-secondary"
                :href="r.attachment_url"
                target="_blank"
              >
                Ver
              </b-button>
            </div>
          </div>
        </div>

        <div
          v-if="r.status === 'rejected' && r.reject_reason"
          class="mt-25"
        >
          <small class="text-danger">
            <strong>Motivo:</strong> {{ r.reject_reason }}
          </small>
        </div>

        <!-- Admin actions -->
        <div
          v-if="isAdmin && r.status === 'pending'"
          class="mt-50 d-flex"
        >
          <b-button
            size="sm"
            variant="success"
            class="mr-50"
            :disabled="approvingId===r.id"
            @click="approve(r)"
          >
            Aprobar y abonar
          </b-button>
          <b-button
            size="sm"
            variant="outline-danger"
            :disabled="rejectingId===r.id"
            @click="openReject(r)"
          >
            Rechazar
          </b-button>
        </div>
      </div>
    </div>

    <b-alert
      v-else
      variant="light"
      show
    >
      Aún no hay comprobantes.
    </b-alert>

    <!-- Modal reject -->
    <b-modal
      :id="`reject-receipt-modal-${customerId}`"
      title="Rechazar comprobante"
      @ok="confirmReject"
    >
      <b-form-textarea
        v-model="rejectReason"
        rows="3"
        placeholder="Motivo del rechazo"
      />
    </b-modal>
  </b-card>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const RECEIPT_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
const MAX_RECEIPT_BYTES = 7 * 1024 * 1024

export default {
  props: {
    customerId: { type: [String, Number], required: true },
    walletId: { type: [String, Number], required: false },
    isAdmin: { type: Boolean, default: false },
    receipts: { type: Array, default: () => [] },
  },

  data() {
    return {
      file: null,
      fileInputKey: 0,
      amount: null,
      note: '',
      uploading: false,
      approvingId: null,
      rejectingId: null,
      rejectReason: '',
      rejectTarget: null,
    }
  },
  computed: {
    canUpload() { return true },
    hasAmount() {
      return Number(this.amount) >= 0.01
    },
    canApply() {
      return this.hasAmount && Boolean(this.file) && !this.uploading
    },
    applyHint() {
      if (this.uploading) return ''
      if (!this.hasAmount && !this.file) return 'Escribe el monto y selecciona el comprobante para poder aplicar.'
      if (!this.hasAmount) return 'Escribe el monto de la transferencia.'
      if (!this.file) return 'Selecciona el comprobante.'
      return ''
    },
    pendingCount() {
      return this.receipts.filter(r => r.status === 'pending').length
    },
  },
  methods: {
    badgeVariant(status) {
      if (status === 'approved') return 'success'
      if (status === 'rejected') return 'danger'
      return 'warning'
    },
    formatMoney(n) {
      try {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n || 0))
      } catch {
        return `$${Number(n || 0).toFixed(2)}`
      }
    },
    toast(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'SaveIcon', variant },
      })
    },

    resetFile() {
      this.file = null
      this.fileInputKey += 1
    },

    checkFile(file) {
      if (!file) return
      if (!RECEIPT_TYPES.includes(file.type)) {
        this.toast('El comprobante debe ser JPG, PNG o PDF', 'warning')
        this.resetFile()
        return
      }
      if (file.size > MAX_RECEIPT_BYTES) {
        this.toast('El comprobante pesa más de 7 MB; usa uno más ligero', 'warning')
        this.resetFile()
      }
    },

    async applyReceipt() {
      if (!this.canApply) return
      if (!this.walletId) {
        this.toast('No se encontró walletId para subir el comprobante', 'warning')
        return
      }

      try {
        this.uploading = true
        const base64 = await this.$getBase64(this.file)

        await this.$store.dispatch('wallet/uploadTransferReceiptByWalletId', {
          walletId: this.walletId,
          receipt: {
            amount: Number(this.amount),
            note: this.note,
            transfer_attachment: { data: base64 },
          },
        })

        await this.$store.dispatch('wallet/fetchTransferReceiptsByWalletId', {
          walletId: this.walletId,
          customerId: this.customerId,
        })

        this.toast('Comprobante enviado; queda pendiente de revisión', 'success')
        this.resetFile()
        this.amount = null
        this.note = ''
      } catch (e) {
        this.toast(e?.response?.data?.messages?.[0] || e?.response?.data?.error || 'No se pudo enviar el comprobante', 'danger')
      } finally {
        this.uploading = false
      }
    },

    async approve(r) {
      if (!this.walletId) {
        this.toast('No se encontró walletId', 'warning')
        return
      }

      try {
        this.approvingId = r.id

        // El backend aprueba y abona en la misma transacción: si se hicieran como
        // dos llamadas, una falla a medias dejaba el comprobante aprobado sin abono.
        await this.$store.dispatch('wallet/approveTransferReceipt', { receiptId: r.id })

        await this.$store.dispatch('wallet/fetchTransferReceiptsByWalletId', {
          walletId: this.walletId,
          customerId: this.customerId,
        })

        this.$emit('refresh-wallet')
        this.toast('Comprobante aprobado y abono aplicado', 'success')
      } catch (e) {
        this.toast(e?.response?.data?.error || e?.message || 'Error al aprobar', 'danger')
      } finally {
        this.approvingId = null
      }
    },

    openReject(r) {
      this.rejectTarget = r
      this.rejectReason = ''
      this.$bvModal.show(`reject-receipt-modal-${this.customerId}`)
    },

    async confirmReject() {
      if (!this.walletId) {
        this.toast('No se encontró walletId', 'warning')
        return
      }

      if (!this.rejectTarget) return
      try {
        this.rejectingId = this.rejectTarget.id
        await this.$store.dispatch('wallet/rejectTransferReceipt', {
          receiptId: this.rejectTarget.id,
          reason: this.rejectReason,
        })
        await this.$store.dispatch('wallet/fetchTransferReceiptsByWalletId', {
          walletId: this.walletId,
          customerId: this.customerId,
        })
        this.toast('Comprobante rechazado', 'warning')
      } catch (e) {
        this.toast(e?.response?.data?.error || e?.message || 'Error al rechazar', 'danger')
      } finally {
        this.rejectingId = null
        this.rejectTarget = null
      }
    },
  },
}
</script>
